import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { airlinesAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { 
    Plane, 
    Lock, 
    ExternalLink, 
    Search,
    CheckCircle,
    XCircle,
    Loader2,
    Crown,
    Filter
} from 'lucide-react';
import { toast } from 'sonner';

export default function Airlines() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [airlines, setAirlines] = useState(null);
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterAccepting, setFilterAccepting] = useState(false);
    const [hasAccess, setHasAccess] = useState(false);

    useEffect(() => {
        loadAirlines();
    }, [user]);

    const loadAirlines = async () => {
        try {
            // Check if user has premium access
            if (user?.membership_type === 'membership_premium') {
                const res = await airlinesAPI.getHiringAirlines();
                setAirlines(res.data);
                setHasAccess(true);
            } else {
                // Load preview for non-premium users
                const res = await airlinesAPI.getPreview();
                setPreview(res.data);
                setHasAccess(false);
            }
        } catch (err) {
            // If 403, user doesn't have access
            if (err.response?.status === 403) {
                try {
                    const res = await airlinesAPI.getPreview();
                    setPreview(res.data);
                } catch (e) {
                    console.error('Failed to load preview:', e);
                }
                setHasAccess(false);
            } else {
                console.error('Failed to load airlines:', err);
            }
        } finally {
            setLoading(false);
        }
    };

    const filterAirlines = (airlineList) => {
        return airlineList.filter(airline => {
            const matchesSearch = airline.name.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesFilter = !filterAccepting || airline.accepting;
            return matchesSearch && matchesFilter;
        });
    };

    const countAccepting = () => {
        if (!airlines) return 0;
        return Object.values(airlines).reduce((total, category) => {
            return total + category.airlines.filter(a => a.accepting).length;
        }, 0);
    };

    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    // Show locked view for non-premium users
    if (!hasAccess) {
        return (
            <div className="min-h-screen">
                <section className="py-16 md:py-24">
                    <div className="container mx-auto px-4">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="text-center max-w-2xl mx-auto"
                        >
                            <div className="h-20 w-20 rounded-full bg-yellow-500/10 flex items-center justify-center mx-auto mb-6">
                                <Lock className="h-10 w-10 text-yellow-600" />
                            </div>
                            
                            <Badge variant="secondary" className="mb-4">
                                <Crown className="h-3 w-3 mr-1" />
                                Premium Feature
                            </Badge>
                            
                            <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mb-4">
                                Airline Hiring Links
                            </h1>
                            
                            <p className="text-lg text-muted-foreground mb-8">
                                Get direct links to {preview?.total_airlines || '40+'}  airlines with real-time 
                                hiring status. Currently {preview?.accepting_applications || '15+'}  airlines are 
                                accepting applications! <strong>Updated bi-weekly (twice a month)</strong> for accuracy.
                            </p>

                            {/* Preview Stats */}
                            <div className="grid grid-cols-2 gap-4 mb-8">
                                <Card className="p-4">
                                    <div className="text-3xl font-bold text-primary">{preview?.total_airlines || '40+'}</div>
                                    <div className="text-sm text-muted-foreground">Total Airlines</div>
                                </Card>
                                <Card className="p-4">
                                    <div className="text-3xl font-bold text-green-500">{preview?.accepting_applications || '15+'}</div>
                                    <div className="text-sm text-muted-foreground">Now Hiring</div>
                                </Card>
                            </div>

                            {/* Categories Preview */}
                            <Card className="p-6 mb-8 text-left">
                                <h3 className="font-semibold mb-4">Airlines Categories:</h3>
                                <div className="flex flex-wrap gap-2">
                                    {['Legacy Carriers', 'Regional Airlines', 'Low-Cost Carriers', 'Charter Airlines', 'Specialty Airlines'].map(cat => (
                                        <Badge key={cat} variant="outline">{cat}</Badge>
                                    ))}
                                </div>
                                <p className="text-sm text-muted-foreground mt-4">
                                    Including: Delta, United, American, Southwest, JetBlue, Alaska, and many more...
                                </p>
                            </Card>

                            <Button 
                                size="lg"
                                className="rounded-full bg-yellow-500 hover:bg-yellow-600 gap-2"
                                onClick={() => navigate('/membership')}
                                data-testid="get-premium-btn"
                            >
                                <Crown className="h-5 w-5" />
                                Get Premium Access - $49.99/month
                            </Button>
                            
                            <p className="text-sm text-muted-foreground mt-4">
                                Unlock access to all airline links and hiring status
                            </p>
                        </motion.div>
                    </div>
                </section>
            </div>
        );
    }

    // Show full airline list for premium users
    return (
        <div className="min-h-screen">
            <section className="py-8 md:py-12">
                <div className="container mx-auto px-4">
                    {/* Header */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-8"
                    >
                        <div className="flex items-center gap-2 mb-2">
                            <Plane className="h-6 w-6 text-primary" />
                            <Badge className="bg-yellow-500">
                                <Crown className="h-3 w-3 mr-1" />
                                Premium Access
                            </Badge>
                        </div>
                        <h1 className="font-heading text-3xl md:text-4xl font-bold mb-2">
                            Airline Hiring Links
                        </h1>
                        <p className="text-muted-foreground">
                            Direct application links to airlines hiring flight attendants. <strong>Updated bi-weekly (twice a month)</strong> for accuracy.
                        </p>
                    </motion.div>

                    {/* Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                        <Card className="p-4 text-center">
                            <div className="text-2xl font-bold">
                                {Object.values(airlines).reduce((t, c) => t + c.airlines.length, 0)}
                            </div>
                            <div className="text-sm text-muted-foreground">Total Airlines</div>
                        </Card>
                        <Card className="p-4 text-center">
                            <div className="text-2xl font-bold text-green-500">{countAccepting()}</div>
                            <div className="text-sm text-muted-foreground">Now Hiring</div>
                        </Card>
                        <Card className="p-4 text-center">
                            <div className="text-2xl font-bold">{Object.keys(airlines).length}</div>
                            <div className="text-sm text-muted-foreground">Categories</div>
                        </Card>
                        <Card className="p-4 text-center">
                            <div className="text-2xl font-bold text-primary">Live</div>
                            <div className="text-sm text-muted-foreground">Updated</div>
                        </Card>
                    </div>

                    {/* Search & Filter */}
                    <div className="flex flex-col sm:flex-row gap-4 mb-8">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Search airlines..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10"
                                data-testid="airline-search"
                            />
                        </div>
                        <Button
                            variant={filterAccepting ? "default" : "outline"}
                            onClick={() => setFilterAccepting(!filterAccepting)}
                            className="rounded-full gap-2"
                            data-testid="filter-hiring"
                        >
                            <Filter className="h-4 w-4" />
                            {filterAccepting ? 'Show All' : 'Now Hiring Only'}
                        </Button>
                    </div>

                    {/* Airlines List */}
                    <div className="space-y-8">
                        {Object.entries(airlines).map(([key, category]) => {
                            const filteredAirlines = filterAirlines(category.airlines);
                            if (filteredAirlines.length === 0) return null;
                            
                            return (
                                <motion.div
                                    key={key}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                >
                                    <h2 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2">
                                        {category.title}
                                        <Badge variant="secondary">{filteredAirlines.length}</Badge>
                                    </h2>
                                    
                                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {filteredAirlines.map((airline) => (
                                            <Card 
                                                key={airline.name}
                                                className={`p-4 hover:shadow-lg transition-shadow ${airline.accepting ? 'border-green-500/50' : ''}`}
                                            >
                                                <div className="flex items-start justify-between mb-3">
                                                    <div className="flex items-center gap-2">
                                                        <Plane className="h-5 w-5 text-primary" />
                                                        <span className="font-medium">{airline.name}</span>
                                                    </div>
                                                    {airline.accepting ? (
                                                        <Badge className="bg-green-500 text-xs">
                                                            <CheckCircle className="h-3 w-3 mr-1" />
                                                            Hiring
                                                        </Badge>
                                                    ) : (
                                                        <Badge variant="secondary" className="text-xs">
                                                            <XCircle className="h-3 w-3 mr-1" />
                                                            Closed
                                                        </Badge>
                                                    )}
                                                </div>
                                                
                                                {airline.note && (
                                                    <p className="text-xs text-muted-foreground mb-3">
                                                        Bases: {airline.note}
                                                    </p>
                                                )}
                                                
                                                <a
                                                    href={airline.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                                                    data-testid={`airline-link-${airline.name.replace(/\s+/g, '-').toLowerCase()}`}
                                                >
                                                    Apply Now
                                                    <ExternalLink className="h-3 w-3" />
                                                </a>
                                            </Card>
                                        ))}
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>

                    {/* Empty State */}
                    {searchTerm && Object.values(airlines).every(cat => filterAirlines(cat.airlines).length === 0) && (
                        <Card className="p-8 text-center">
                            <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                            <h3 className="font-semibold mb-2">No airlines found</h3>
                            <p className="text-muted-foreground">
                                Try a different search term or clear the filter
                            </p>
                        </Card>
                    )}
                </div>
            </section>
        </div>
    );
}
